import type { FormField } from "@/lib/database";

export type VisibilityResult = Record<
	string,
	{ visible: boolean; disabled: boolean }
>;

type Condition = NonNullable<
	NonNullable<FormField["settings"]>["conditionalVisibility"]
>["conditions"][number];

function normalizeValue(value: unknown): string {
	if (value === null || value === undefined) return "";
	if (Array.isArray(value)) return value.map(String).join(",");
	return String(value);
}

function evaluateCondition(condition: Condition, formData: Record<string, any>): boolean {
	const actual = formData[condition.fieldId];
	const expected = condition.value;

	switch (condition.operator) {
		case "equals":
			return normalizeValue(actual) === normalizeValue(expected);
		case "not_equals":
			return normalizeValue(actual) !== normalizeValue(expected);
		case "contains":
			return normalizeValue(actual).includes(normalizeValue(expected));
		case "not_contains":
			return !normalizeValue(actual).includes(normalizeValue(expected));
		case "is_checked": {
			if (Array.isArray(actual)) return actual.length > 0;
			return Boolean(actual);
		}
		case "is_not_checked": {
			if (Array.isArray(actual)) return actual.length === 0;
			return !Boolean(actual);
		}
		case "greater_than": {
			const a = Number(actual);
			const b = Number(expected);
			if (Number.isNaN(a) || Number.isNaN(b)) return false;
			return a > b;
		}
		case "less_than": {
			const a = Number(actual);
			const b = Number(expected);
			if (Number.isNaN(a) || Number.isNaN(b)) return false;
			return a < b;
		}
		default:
			return true;
	}
}

/** Оценка одного условия на произвольном «срезе данных» (ключ → значение).
 * Используется и для полей формы (formData), и для колонок строки таблицы
 * (rowValues) в table-field (t_82191af7). */
export function evaluateConditions(
	logic: NonNullable<
		NonNullable<FormField["settings"]>["conditionalVisibility"]
	>,
	data: Record<string, any>,
): boolean {
	if (!logic.enabled || logic.conditions.length === 0) return true;
	const evaluated = logic.conditions.map((condition) =>
		evaluateCondition(condition, data),
	);
	return logic.match === "all" ? evaluated.every(Boolean) : evaluated.some(Boolean);
}

/** Оценивает условную видимость для всех полей формы. */
export function evaluateFieldVisibility(
	fields: FormField[],
	formData: Record<string, any>,
): VisibilityResult {
	const result: VisibilityResult = {};

	for (const field of fields) {
		const logic = field.settings?.conditionalVisibility;
		let visible = true;

		if (logic?.enabled && logic.conditions.length > 0) {
			const evaluated = logic.conditions.map((condition) =>
				evaluateCondition(condition, formData)
			);
			visible = logic.match === "all"
				? evaluated.every(Boolean)
				: evaluated.some(Boolean);
		}

		result[field.id] = { visible, disabled: false };
	}

	return result;
}

/** Возвращает только видимые поля (учитывая conditionalVisibility). */
export function getVisibleFields(
	fields: FormField[],
	visibility: VisibilityResult,
): FormField[] {
	return fields.filter((field) => visibility[field.id]?.visible !== false);
}
